import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";

// Constants
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000/v1/api";

/**
 * Custom API client with auth token handling and error management
 */
class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        "Content-Type": "application/json",
      },
      timeout: 30000, // 30 seconds
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        // Handle unauthorized errors (expired token)
        if (
          error.response?.status === 401 &&
          error.response.config.url !== "/auth/login-encrypted"
        ) {
          localStorage.removeItem("token");

          // window.location.href = "/login?expired=true";
          return Promise.reject(
            new Error("Session expired. Please login again."),
          );
        }

        // Handle other errors
        const errorMessage =
          (error.response?.data as { detail?: string })?.detail ||
          error.message ||
          "An error occurred";

        return Promise.reject(new Error(errorMessage));
      },
    );
  }

  // Generic request method
  async request<T>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.request(config);

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Helper methods for different HTTP methods
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: "GET", url });
  }

  async post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request<T>({ ...config, method: "POST", url, data });
  }

  async put<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request<T>({ ...config, method: "PUT", url, data });
  }

  async patch<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request<T>({ ...config, method: "PATCH", url, data });
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: "DELETE", url });
  }
}

// Create singleton instance
const apiClient = new ApiClient();

export default apiClient;
